import { point } from '@turf/turf';
import { Feature } from 'app/model/mapping/drawing/Feature';
import { DoNothing, Handler, SimpleHandler, TypedHandler } from "app/types/handlers";
import { GeoPoint } from 'geodesy-js';
import { Angle, Length } from 'unitsnet-js';

export interface PixoPt {
  x: number;
  y: number;
  z?: number;
  convert: () => SpatialCoord;
}
export interface SpatialCoord {
  lng: number;
  lat: number;
  accuracy?: number;
  timestamp?: number;
  altitude?: number;
  altitudeAccuracy?: number;
  heading?: number;
  speed?: number;
  convert: () => PixoPt;
}
export interface PixoSpatialCoord {
  pixo: PixoPt;
  spatial: SpatialCoord;
  Project: SimpleHandler;
  UnProject: SimpleHandler;
  UpdateSpatial: (lng: number, lat: number, accuracy?: number | null, timestamp?: number | null,
    altitude?: number | null, altitudeAccuracy?: number | null, heading?: number | null, speed?: number | null) => void;
  UpdatePixo: (x: number, y: number, z?: number) => void;
  PostConvert?: SimpleHandler;
}

export type PixoSpatialProjectFunction = (spatialCoord: SpatialCoord) => PixoPt;
export type PixoSpatialUnprojectFunction = (pxPt: PixoPt) => SpatialCoord;

export class PixoSpatial {
  private pProjectHandler?: Handler<SpatialCoord, PixoPt>;
  private pUnprojectHandler?: Handler<PixoPt, SpatialCoord>;
  private pIsDetatched = true;

  readonly detatched?: boolean;
  get detached() {
    return this.pIsDetatched;
  }
  constructor() { }

  Project(spatialCoord: SpatialCoord): PixoPt {
    if (!this.pProjectHandler) throw new Error('attempting to project points before setting a projection handler')
    return this.pProjectHandler(spatialCoord);
  }

  UnProject(pxPt: PixoPt): SpatialCoord {
    if (!this.pUnprojectHandler) throw new Error('attempting to unproject points before setting an unprojection handler')
    return this.pUnprojectHandler(pxPt);
  }
  PixoSpatialCoord(startItem: SpatialCoord | PixoPt): PixoSpatialCoord {
    let coord: PixoSpatialCoord;

    if (startItem.hasOwnProperty('lng')) {
      coord = {
        pixo: (startItem as SpatialCoord).convert(),
        spatial: startItem as SpatialCoord,
        Project: DoNothing,
        UnProject: DoNothing,
        UpdateSpatial: DoNothing,
        UpdatePixo: DoNothing
      };
    } else {
      coord = {
        pixo: startItem as PixoPt,
        spatial: (startItem as PixoPt).convert(),
        Project: DoNothing,
        UnProject: DoNothing,
        UpdateSpatial: DoNothing,
        UpdatePixo: DoNothing
      };
    }
    coord.Project = () => {
      coord.spatial = coord.pixo.convert();
    };
    coord.UnProject = () => {
      coord.pixo = coord.spatial.convert();
    };
    coord.UpdateSpatial = (
      lng: number, lat: number, accuracy?: number | null, timestamp?: number | null,
      altitude?: number | null, altitudeAccuracy?: number | null, heading?: number | null, speed?: number | null, mostAccurate: boolean = false
    ) => {
      if (mostAccurate === true) {
        if (coord.spatial.accuracy && coord.spatial.accuracy > 0) {
          if (!!accuracy && (accuracy > coord.spatial.accuracy) && (accuracy > 0)) {
            return;
          }
        }
      }
      coord.spatial.lng = lng;
      coord.spatial.lat = lat;
      if (!!accuracy) {
        coord.spatial.accuracy = accuracy;
      }
      if (!!timestamp) {
        coord.spatial.timestamp = timestamp;
      }
      if (!!altitude) {
        coord.spatial.altitude = altitude;
      }
      if (!!altitudeAccuracy) {
        coord.spatial.altitudeAccuracy = altitudeAccuracy;
      }
      if (!!heading) {
        coord.spatial.heading = heading;
      }
      if (!!speed) {
        coord.spatial.speed = speed;
      }
      coord.UnProject();
      if (!!coord.PostConvert) {
        coord.PostConvert();
      }
    };
    coord.UpdatePixo = (x: number, y: number, z?: number) => {
      coord.pixo.x = x;
      coord.pixo.y = y;
      if (!!z) {
        coord.pixo.z = z;
      }
      coord.Project();
    };
    return coord;
  }
  PixoPt(ptObj: any, xProp: string = 'x', yProp: string = 'y', zProp: string = 'z'): PixoPt {
    const pixoPt: any = {
      x: !!ptObj[xProp] ? ptObj[xProp] : 0,
      y: !!ptObj[yProp] ? ptObj[yProp] : 0,
      convert: null
    };
    pixoPt.convert = (): SpatialCoord => {
      return this.UnProject(pixoPt as PixoPt);
    };
    if (!!pixoPt[zProp]) {
      pixoPt.z = pixoPt[zProp];
    }
    return pixoPt as PixoPt;
  }
  SpatialCoord(coord: any, lngProp: string = 'lng', latProp: string = 'lat'): SpatialCoord {
    const spatialCoord: any = {
      lng: !!coord[lngProp] ? coord[lngProp] : 0,
      lat: !!coord[latProp] ? coord[latProp] : 0,
      convert: null
    };
    spatialCoord.convert = (): PixoPt => {
      return this.Project(spatialCoord);
    };
    return spatialCoord as SpatialCoord;
  }
  Attach(
    project: PixoSpatialProjectFunction,
    unproject: PixoSpatialUnprojectFunction
  ) {
    this.pProjectHandler = project;
    this.pUnprojectHandler = unproject;
    this.pIsDetatched = true;
  }
  Detatch() {
    this.pProjectHandler = undefined;
    this.pUnprojectHandler = undefined;
    this.pIsDetatched = true;
  }

}

export function PixoSpatialToGeoPoint(coord: PixoSpatialCoord): GeoPoint {
  return {
    Longitude: Angle.FromDegrees(coord.spatial.lng),
    Latitude: Angle.FromDegrees(coord.spatial.lat)
  }
}
